<template>
    <v-layout class="billing-address" row wrap>
        <v-flex xs12>
            <p class="body-2 ma-0">{{ 'Address' | translation }}*</p>
            <vuetify-google-autocomplete
                id="billing_autocomplete"
                @placechanged="onAutocompleteChanged"
                :placeholder="$language.translate('Addresse eingeben...')"
                :disabled="isDisabled"
                :solo="inputSolo"
                :error-messages="addressErrors"
                hide-no-data
                no-filter
                :city.sync="city"
                :street.sync="street"
                :houseNumber.sync="houseNumber"
                :postCode.sync="postCode"
                :country.sync="country"
            ></vuetify-google-autocomplete>
        </v-flex>

        <v-flex xs6>
            <p class="subheading ma-0">{{ firstName }} {{ lastName }}</p>
            <p class="subheading ma-0">{{ street }} {{ houseNumber }}</p>
            <p class="subheading ma-0">{{ postCode }} {{ city }}</p>
            <p class="subheading ma-0">{{ country | translation }}</p>
        </v-flex>
        <v-flex xs6 text-xs-right>
            <a href="#" @click.prevent="showAddressForm"> {{ 'Address not found?' | translation }}? </a>
        </v-flex>

        <address-form-modal
            v-model="displayAddressForm"
            :form-class="formClass"
            :display-incomplete-address-error="displayIncompleteAddressError"
            :incomplete-address-error="incompleteAddressError"
            :display-house-number-field="displayHouseNumberField"
            :input-label-outside="inputLabelOutside"
            :is-disabled="isDisabled"
            :input-solo="inputSolo"
            :button-round="buttonRound"
            :button-depressed="buttonDepressed"
            :button-large="buttonLarge"
            @submit="applyAddressForm"
            :address-fields.sync="addressFields"
        ></address-form-modal>
    </v-layout>
</template>

<script>
import module from './store/billingAddress';
import { createNamespacedHelpers, mapState, mapGetters, mapActions } from 'vuex';
import Vue from 'vue';
import VuetifyGoogleAutocomplete from '@breakmedia/vuetify-google-autocomplete';
import AddressFormModal from '@breakmedia/prime-vue-checkout/src/AddressStep/AddressFormModal';

const moduleName = 'BillingAddressForm';
const loginModuleName = 'Login';
const { mapMutations } = createNamespacedHelpers(moduleName);

if (!Vue.$store.state[moduleName]) {
    Vue.$store.registerModule(moduleName, module);
}

export default {
    name: moduleName,
    components: {
        AddressFormModal,
    },
    created() {
        Vue.use(VuetifyGoogleAutocomplete, {
            apiKey: Vue.$config.VUE_APP_GOOGLE_MAPS_PLACES_API_KEY,
        });
        if (this.$store.state.Registration) {
            //If Created for this module is triggered after Registration is mounted no watch will be triggered.
            //To handle such case we fill all data manually
            //ToDo: make one mapper and do fill and watch in cycles
            this.setFirstName(this.$store.getters['Registration/getFirstName']);
            this.setLastName(this.$store.getters['Registration/getLastName']);
            this.setSalutation(this.$store.getters['Registration/getSalutation']);
            this.setPackingStation(this.$store.getters['Registration/getPackingStation']);
            this.setPackingStationPostCode(this.$store.getters['Registration/getPackingStationPostCode']);
            this.setFiscalCode(this.$store.getters['Registration/getFiscalCode']);
            this.setPhone(this.$store.getters['Registration/getPhone']);
            this.setCompany(this.$store.getters['Registration/getCompany']);
            this.setUid(this.$store.getters['Registration/getUid']);

            //To keep store independent we map registration-form's fields to billing-address store
            this.$store.watch(
                () => this.$store.getters['Registration/getFirstName'],
                value => {
                    this.setFirstName(value);
                },
            );
            this.$store.watch(
                () => this.$store.getters['Registration/getLastName'],
                value => {
                    this.setLastName(value);
                },
            );
            this.$store.watch(
                () => this.$store.getters['Registration/getSalutation'],
                value => {
                    this.setSalutation(value);
                },
            );
            this.$store.watch(
                () => this.$store.getters['Registration/getPackingStation'],
                value => {
                    this.setPackingStation(value);
                },
            );
            this.$store.watch(
                () => this.$store.getters['Registration/getPackingStationPostCode'],
                value => {
                    this.setPackingStationPostCode(value);
                },
            );
            this.$store.watch(
                () => this.$store.getters['Registration/getFiscalCode'],
                value => {
                    this.setFiscalCode(value);
                },
            );
            this.$store.watch(
                () => this.$store.getters['Registration/getPhone'],
                value => {
                    this.setPhone(value);
                },
            );
            this.$store.watch(
                () => this.$store.getters['Registration/getCompany'],
                value => {
                    this.setCompany(value);
                },
            );
            this.$store.watch(
                () => this.$store.getters['Registration/getUid'],
                value => {
                    this.setUid(value);
                },
            );
        }

        this.loadAddress();
    },
    props: {
        disabled: Boolean,
        houseNumberRequired: Boolean,
        displayAddressFormProp: Boolean,
        inputSolo: Boolean,
        inputLabelOutside: Boolean,
        buttonDepressed: Boolean,
        buttonRound: Boolean,
        buttonLarge: Boolean,
        formClass: String,
    },
    data() {
        return {
            displayAddressForm: false,
            salutations: [
                {
                    text: 'Frau',
                    value: 'frau',
                },
                {
                    text: 'Herr',
                    value: 'herr',
                },
            ],
            addressErrors: [],
            incompleteAddressError: 'The found address is not complete. Please fill the missing data manually.',
            displayIncompleteAddressError: false,
        };
    },
    computed: {
        ...mapState(moduleName, ['loading', 'countries']),
        ...mapGetters(loginModuleName, ['isUserLoggedIn']),
        firstName: {
            get() {
                return this.getFirstName();
            },
            set(value) {
                this.setFirstName(value);
            },
        },
        lastName: {
            get() {
                return this.getLastName();
            },
            set(value) {
                this.setLastName(value);
            },
        },
        isDisabled() {
            return this.disabled || this.loading;
        },
        street: {
            get() {
                return this.getStreet();
            },
            set(value) {
                this.setStreet(value);
            },
        },
        postCode: {
            get() {
                return this.getPostCode();
            },
            set(value) {
                this.setPostCode(value);
            },
        },
        city: {
            get() {
                return this.getCity();
            },
            set(value) {
                this.setCity(value);
            },
        },
        salutation: {
            get() {
                return this.getSalutation();
            },
            set(value) {
                this.setSalutation(value);
            },
        },
        phone: {
            get() {
                return this.getPhone();
            },
            set(value) {
                this.setPhone(value);
            },
        },
        company: {
            get() {
                return this.getCompany();
            },
            set(value) {
                this.setCompany(value);
            },
        },
        uid: {
            get() {
                return this.getUid();
            },
            set(value) {
                this.setUid(value);
            },
        },
        country: {
            get() {
                return this.$country.getCountryNameBy('iso_2', this.getCountry());
            },
            set(value) {
                this.setCountry(value);
            },
        },
        packingStation: {
            get() {
                return this.getPackingStation();
            },
            set(value) {
                this.setPackingStation(value);
            },
        },
        packingStationPostCode: {
            get() {
                return this.getPackingStationPostCode();
            },
            set(value) {
                this.setPackingStationPostCode(value);
            },
        },
        fiscalCode: {
            get() {
                return this.getFiscalCode();
            },
            set(value) {
                this.setFiscalCode(value);
            },
        },
        houseNumber: {
            get() {
                return this.getHouseNumber();
            },
            set(value) {
                this.setHouseNumber(value);
            },
        },
        displayHouseNumberField() {
            let result = this.houseNumberRequired || Boolean(this.houseNumber);

            if (result && !this.houseNumber && this.street) {
                let match = this.street.match(/\s\d+([a-z]*|\/\d+[a-z]*)$/gm);
                if (match && match.length) {
                    this.houseNumber = match[0].trim();
                    this.street = this.street.replace(/\s\d+([a-z]*|\/\d+[a-z]*)$/gm, '');
                }
            }

            return result;
        },
        addressFields: {
            get() {
                let addressFields = {
                    street: this.street,
                    city: this.city,
                    postCode: this.postCode,
                    country: this.getCountry(),
                };

                if (this.displayHouseNumberField) {
                    addressFields.houseNumber = this.houseNumber;
                }

                return addressFields;
            },
            set(value) {
                Object.keys(value).forEach(key => {
                    this[key] = value[key];
                });
            },
        },
    },
    methods: {
        ...mapGetters(moduleName, [
            'getFirstName',
            'getLastName',
            'getStreet',
            'getPostCode',
            'getCity',
            'getSalutation',
            'getCountry',
            'getPackingStation',
            'getPackingStationPostCode',
            'getFiscalCode',
            'getHouseNumber',
            'getPhone',
            'getCompany',
            'getUid',
        ]),
        ...mapMutations([
            'setFirstName',
            'setLastName',
            'setStreet',
            'setPostCode',
            'setCity',
            'setSalutation',
            'setCountry',
            'resetAddress',
            'setPackingStation',
            'setPackingStationPostCode',
            'setFiscalCode',
            'setHouseNumber',
            'setPhone',
            'setCompany',
            'setUid',
        ]),
        ...mapActions(moduleName, {
            loadAddressAction: 'loadAddress',
            saveAddressAction: 'saveAddress',
        }),
        loadAddress() {
            return this.loadAddressAction().then(result => {
                if (!this.isUserLoggedIn) {
                    result = this.$session.get('customer-address-billing') || {};
                    this.addressFields = result;
                }
                this.$emit('addressLoaded', result);
                if (this.$store.state.Registration) {
                    //Set the values to registration form when address is loaded from the server
                    this.$store.commit('Registration/setFirstName', this.firstName);
                    this.$store.commit('Registration/setLastName', this.lastName);
                    this.$store.commit('Registration/setSalutation', this.salutation);
                    this.$store.commit('Registration/setPackingStation', this.packingStation);
                    this.$store.commit('Registration/setPackingStationPostCode', this.packingStationPostCode);
                    this.$store.commit('Registration/setFiscalCode', this.fiscalCode);
                    this.$store.commit('Registration/setPhone', this.phone);
                    this.$store.commit('Registration/setCompany', this.company);
                    this.$store.commit('Registration/setUid', this.uid);
                }
            });
        },
        saveAddress() {
            return this.saveAddressAction();
        },
        onAutocompleteChanged() {
            this.$emit('validateUid');
            if (this.isAddressValid() !== true) {
                this.showAddressForm(true);
            }
        },
        validate() {
            let valid = this.isAddressValid();
            if (valid === true) {
                this.addressErrors = [];
                this.displayIncompleteAddressError = false;
                this.$emit('validateUid');
                return valid;
            } else {
                this.addressErrors = [valid];
                return false;
            }
        },
        showAddressForm(displayIncompleteAddressError = false) {
            if (!this.isDisabled) {
                this.displayIncompleteAddressError =
                    typeof displayIncompleteAddressError === 'boolean' ? displayIncompleteAddressError : false;
                this.displayAddressForm = true;
            }
        },
        applyAddressForm() {
            this.displayAddressForm = false;
            this.validate();
            this.$emit('submit');
        },
        isAddressValid() {
            if (!this.street && !this.postCode && !this.city) {
                return 'You need to fill the address';
            } else if (!this.street) {
                return 'Street is missing';
            } else if (!this.postCode) {
                return 'Post code is missing';
            } else if (!this.city) {
                return 'City is missing';
            } else if (!this.country) {
                return 'Country is missing';
            } else if (this.displayHouseNumberField && !this.houseNumber) {
                return 'House number is missing';
            }

            return true;
        },
    },
    watch: {
        isUserLoggedIn(value) {
            if (value) {
                this.loadAddress();
            } else {
                this.resetAddress();
                this.$refs.form.resetValidation();
            }
        },
        displayAddressFormProp(value) {
            this.displayAddressForm = value;
        },
        street(val) {
            if (!this.displayHouseNumberField && this.houseNumber && val) {
                this.street = val + this.houseNumber;
                this.houseNumber = null;
            }
        },
        houseNumber(val) {
            if (!this.displayHouseNumberField && this.street && val) {
                this.street += val;
                this.houseNumber = null;
            }
        },
    },
};
</script>
